import React, { useCallback, useEffect, useRef, useState } from "react";
import { SearchOutlined } from "@ant-design/icons";
import Highlighter from "react-highlight-words";
import { Button, Input, Space, Table, message } from "antd";
import { Link } from "react-router-dom";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { useSelector } from "react-redux";
import vehicleService from "../../service/vehicle.service";
import {
  EMPTY_VEHICLE_FILTERS,
  VEHICLE_LIST_LIMIT,
  buildVehicleListParams,
  canDeleteVehicle,
  canEditVehicle,
  canViewVehicle,
  compareText,
  resolveFilterInputValue,
  resolveFilterSearchKeys,
  sortVehiclesByCreatedAtDesc,
  unwrapVehicleListItems,
} from "../../util/vehicle/vehicle-list-utils";

const DataVehicleTable = ({ refreshKey = 0, clearFiltersKey = 0 }) => {
  const [searchText, setSearchText] = useState("");
  const [searchedColumn, setSearchedColumn] = useState("");
  const [filters, setFilters] = useState(EMPTY_VEHICLE_FILTERS);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableResetKey, setTableResetKey] = useState(0);
  const searchInput = useRef(null);
  const user = useSelector((state) => state.user);

  const canView = canViewVehicle(user);
  const canEdit = canEditVehicle(user);
  const canDelete = canDeleteVehicle(user);

  const fetchData = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const params = buildVehicleListParams(user, filters);
      if (!params) {
        setRows([]);
        return;
      }

      const response = await vehicleService.getAll(params);
      const items = unwrapVehicleListItems(response);
      setRows(sortVehiclesByCreatedAtDesc(items).slice(0, VEHICLE_LIST_LIMIT));
    } catch (error) {
      console.error("Erro ao buscar veículos:", error);
      message.error(
        error?.response?.data?.message ||
          "Não foi possível carregar a lista de veículos.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [user, filters]);

  useEffect(() => {
    fetchData();
  }, [user?.uid, user?.uidShipper, user?.viewALL, fetchData, refreshKey]);

  useEffect(() => {
    if (!clearFiltersKey) return;
    setFilters({ ...EMPTY_VEHICLE_FILTERS });
    setSearchText("");
    setSearchedColumn("");
    setTableResetKey((key) => key + 1);
  }, [clearFiltersKey]);

  const applyColumnFilter = (selectedKeys, confirm, dataIndex) => {
    const value = selectedKeys?.[0] ? String(selectedKeys[0]).trim() : "";
    confirm();
    setSearchText(value);
    setSearchedColumn(dataIndex);
    setFilters((prev) => ({ ...prev, [dataIndex]: value }));
  };

  const clearColumnFilter = (clearFilters, dataIndex) => {
    clearFilters?.();
    setSearchText("");
    setSearchedColumn("");
    setFilters((prev) => ({ ...prev, [dataIndex]: "" }));
  };

  const deleteVehicle = async (id) => {
    if (!window.confirm("Deseja mesmo excluir este veículo?")) return;

    try {
      await vehicleService.delete(id);
      alert("Veículo excluído com sucesso!");
      fetchData();
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          "Não foi possível excluir este veículo.",
      );
    }
  };

  const getColumnSearchProps = (dataIndex) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => {
      const searchKeys = () =>
        resolveFilterSearchKeys(selectedKeys, filters[dataIndex]);

      return (
        <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
          <Input
            ref={searchInput}
            placeholder={`Buscar ${dataIndex}`}
            value={resolveFilterInputValue(selectedKeys, filters[dataIndex])}
            onChange={(e) => setSelectedKeys([e.target.value])}
            onPressEnter={() =>
              applyColumnFilter(searchKeys(), confirm, dataIndex)
            }
            style={{ marginBottom: 8, display: "block" }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                applyColumnFilter(searchKeys(), confirm, dataIndex)
              }
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              Buscar
            </Button>
            <Button
              onClick={() => clearColumnFilter(clearFilters, dataIndex)}
              size="small"
              style={{ width: 90 }}
            >
              Limpar
            </Button>
            <Button type="link" size="small" onClick={() => close()}>
              Fechar
            </Button>
          </Space>
        </div>
      );
    },
    filterIcon: () => (
      <SearchOutlined
        style={{ color: filters[dataIndex] ? "#1677ff" : undefined }}
      />
    ),
    onFilterDropdownOpenChange: (visible) => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: (text) =>
      searchedColumn === dataIndex && searchText ? (
        <Highlighter
          highlightStyle={{ backgroundColor: "#ffc069", padding: 0 }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ""}
        />
      ) : (
        text
      ),
  });

  const columns = [
    {
      title: "Placa",
      dataIndex: "vehiclePlate",
      key: "vehiclePlate",
      ...getColumnSearchProps("vehiclePlate"),
      sorter: (a, b) => compareText(a.vehiclePlate, b.vehiclePlate),
    },
    {
      title: "Descrição",
      dataIndex: "descricaoVeiculo",
      key: "descricaoVeiculo",
      ...getColumnSearchProps("descricaoVeiculo"),
      sorter: (a, b) => compareText(a.descricaoVeiculo, b.descricaoVeiculo),
    },
    {
      title: "Tipo",
      dataIndex: "vehicleType",
      key: "vehicleType",
      ...getColumnSearchProps("vehicleType"),
      sorter: (a, b) => compareText(a.vehicleType, b.vehicleType),
    },
    {
      title: "UF",
      dataIndex: "ufVeiculo",
      key: "ufVeiculo",
      ...getColumnSearchProps("ufVeiculo"),
    },
    {
      title: "Reboques",
      dataIndex: "reboquesCount",
      key: "reboquesCount",
      sorter: (a, b) => (a.reboquesCount || 0) - (b.reboquesCount || 0),
    },
    {
      title: "Ações",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Space size="middle">
          {canEdit ? (
            <Link to={`/vehicle/${record.id}`}>
              <EditOutlinedIcon />
            </Link>
          ) : canView ? (
            <Link to={`/vehicle/${record.id}`} state={{ viewOnly: true }}>
              <VisibilityIcon />
            </Link>
          ) : null}
          {canDelete ? (
            <Link to="#">
              <DeleteOutlinedIcon
                onClick={() => deleteVehicle(record.id)}
                color="error"
              />
            </Link>
          ) : null}
        </Space>
      ),
    },
  ];

  return (
    <Table
      key={tableResetKey}
      rowKey="id"
      columns={columns}
      dataSource={rows}
      loading={loading}
      pagination={{
        pageSize: 10,
        showSizeChanger: true,
        pageSizeOptions: ["10", "20", "50", "100"],
        showTotal: (total) => `${total} veículo(s)`,
      }}
    />
  );
};

export default DataVehicleTable;
